import { useEffect, useCallback } from 'react';

declare global {
    interface Window {
        grecaptcha: {
            ready: (cb: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const SCRIPT_ID = 'recaptcha-v3-script';

const loadScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (document.getElementById(SCRIPT_ID)) {
            if (window.grecaptcha) {
                resolve();
            } else {
                const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement;

                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
            }

            return;
        }
        const script = document.createElement('script');

        script.id = SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
        document.head.appendChild(script);
    });
};

const useRecaptcha = () => {
    const getToken = useCallback(async (action: string): Promise<string> => {
        if (!SITE_KEY) {
            throw new Error('reCAPTCHA site key not configured');
        }

        await loadScript();

        return new Promise((resolve, reject) => {
            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(SITE_KEY, { action });

                    resolve(token);
                } catch (err) {
                    reject(err);
                }
            });
        });
    }, []);

    return { getToken };
};

export default useRecaptcha;
